import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useLessonSequence } from "../../../hooks/useLessonSequence";
import type { StepId, StudentAssetType } from "../../../engine/sequenceEngine";
import type { Lang, Tier } from "../../../api/serving";
import SceneReader from "./SceneReader";
import VocabStep from "./steps/VocabStep";
import EpisodeStep from "./steps/EpisodeStep";
import ActivityStep from "./steps/ActivityStep";
import FormativeAssessment from "./FormativeAssessment";
import MissingChapter from "./shared/MissingChapter";
import { peT } from "../../../i18n/peLesson";

/**
 * LessonSequence — the path-agnostic shell that mounts the locked-sequence
 * engine. The SceneReader is ONE step (`reader`); the engine drives the order.
 *
 * The student sees only completed steps (revisitable) + the current step; later
 * steps are locked until the prior completes. Narration is locked ON inside the
 * reader. Completion persists to pe_assignment_phase_progress via the shared
 * hook. BOTH native and LTI surfaces render this same component (Phase 3).
 */
export interface LessonSequenceProps {
  chapterId: string; // pe_chapters PK (resolved upstream)
  tier: Tier;
  lang: Lang;
  assignmentId: string | null;
  studentId: string | null;
  /**
   * student_assignments row id — the key the /pe/submit rail needs to score the
   * summative quiz. Null ⇒ quiz renders but per-item submission can't persist
   * (degrades, never crashes). Resolved by the host pages.
   */
  studentAssignmentId?: string | null;
  /**
   * Student assets to ship (spec §5 deployment). Undefined ⇒ the hook builds the
   * full CANONICAL_STEPS loop (today's proven default). Passed straight through
   * to the engine, which still tier-gates + order-locks.
   */
  assets?: StudentAssetType[];
  /**
   * Where the completion card's "Back to Dashboard" button navigates. Supplied
   * by the native surface (the student dashboard route); omitted on the LTI
   * surface, where there is no native dashboard inside the Canvas iframe — so
   * the button only renders on the native path.
   */
  dashboardPath?: string;
}

// Run E2 J5 (D04): step labels and every shell string come from the EN/ES
// lesson string table, keyed by the assignment language.
const LessonSequence: React.FC<LessonSequenceProps> = ({
  chapterId,
  tier,
  lang,
  assignmentId,
  studentId,
  studentAssignmentId,
  assets,
  dashboardPath,
}) => {
  const navigate = useNavigate();
  const t = peT(lang);
  const { loading, error, view, completeCurrentStep, saving } = useLessonSequence({
    chapterId,
    tier,
    lang,
    assignmentId,
    studentId,
    assets,
  });

  // Which step the student is currently viewing (may be a completed step they
  // revisit). Defaults to and follows the engine's current step.
  const [viewing, setViewing] = useState<StepId | null>(null);
  // D4 gate: the formative step's "Mark complete & continue" stays hidden until
  // the embedded FormativeAssessment signals every item is answered (onAllDone).
  const [formativeDone, setFormativeDone] = useState(false);
  useEffect(() => {
    if (view) setViewing(view.currentStep ?? view.steps[view.steps.length - 1] ?? null);
  }, [view?.currentStep, view?.isComplete]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto" />
          <p className="mt-4 text-gray-600">{t.loadingLesson}</p>
        </div>
      </div>
    );
  }

  if (error || !view) {
    return <MissingChapter screen="lesson" lang={lang} />;
  }

  const activeStep = viewing ?? view.currentStep;
  const isViewingCurrent = activeStep === view.currentStep;

  return (
    // min-w-0 w-full: on the LTI route this sits in a flex row (mobile); without
    // them the step rail's width pushed the page sideways at 390 px (Run E2 J5).
    <div className="min-h-screen bg-gray-50 min-w-0 w-full">
      {/* Step rail — completed + current visible/clickable, later steps locked. */}
      <nav className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <ol className="max-w-5xl mx-auto flex items-center gap-2 px-4 py-3 overflow-x-auto">
          {view.steps.map((step, i) => {
            const done = view.completed[step];
            const isCurrent = step === view.currentStep;
            const unlocked = view.allowedTransitions.includes(step);
            const isActive = step === activeStep;
            return (
              <li key={step} className="flex items-center gap-2 shrink-0">
                <button
                  type="button"
                  disabled={!unlocked}
                  onClick={() => unlocked && setViewing(step)}
                  aria-current={isActive ? "step" : undefined}
                  className={[
                    "flex items-center gap-2 rounded-full px-3 py-1.5 text-sm font-medium transition",
                    isActive
                      ? "bg-indigo-600 text-white"
                      : unlocked
                        ? "bg-indigo-50 text-indigo-700 hover:bg-indigo-100"
                        : "bg-gray-100 text-gray-400 cursor-not-allowed",
                  ].join(" ")}
                >
                  <span
                    className={[
                      "inline-flex h-5 w-5 items-center justify-center rounded-full text-xs",
                      done
                        ? "bg-green-500 text-white"
                        : isActive
                          ? "bg-white/20 text-white"
                          : "bg-gray-200 text-gray-500",
                    ].join(" ")}
                  >
                    {done ? "✓" : unlocked ? i + 1 : "🔒"}
                  </span>
                  {t.steps[step].label}
                </button>
                {i < view.steps.length - 1 && <span className="text-gray-300">›</span>}
              </li>
            );
          })}
        </ol>
      </nav>

      {/* Current step surface */}
      <main className="max-w-5xl mx-auto px-4 py-6">
        {view.isComplete && (
          <div className="mb-6 rounded-lg bg-green-50 border border-green-200 px-4 py-4 text-green-800">
            <p>{t.allStepsDone}</p>
            {dashboardPath && (
              <button
                type="button"
                onClick={() => navigate(dashboardPath)}
                className="mt-3 rounded-lg bg-green-600 px-5 py-2.5 text-white font-semibold hover:bg-green-700"
              >
                {t.backToDashboard}
              </button>
            )}
          </div>
        )}

        {activeStep === "reader" ? (
          // The real Scene Reader is the `reader` step (narration locked ON).
          <SceneReader chapterId={chapterId} tier={tier} lang={lang} role="student" />
        ) : activeStep === "vocab" ? (
          <VocabStep chapterId={chapterId} tier={tier} lang={lang} />
        ) : activeStep === "video" ? (
          <EpisodeStep chapterId={chapterId} tier={tier} lang={lang} />
        ) : activeStep === "activity" ? (
          <ActivityStep chapterId={chapterId} lang={lang} />
        ) : activeStep === "formative" ? (
          // Native formative step — 1:1 parity with /lti/assessment. SAME
          // component, renderer, and /pe endpoints as the LTI surface: source
          // defaults to the formative path (/pe/assessments). The WS-D serve
          // filter admits the T1 image-MC / tap-to-place / audio_match items
          // (T1 lock holds: no text-MC, no typed). Per-item /pe/submit
          // auto-scores + persists to student_assignments; oral (audio_match)
          // routes to HITL. The host "Mark complete & continue" below owns step
          // advance (FormativeAssessment never writes step progress).
          <FormativeAssessment
            source="assessment"
            role="student"
            embedded
            chapterId={chapterId}
            tier={tier}
            lang={lang}
            studentAssignmentId={studentAssignmentId ?? null}
            onAllDone={() => setFormativeDone(true)}
          />
        ) : activeStep === "summative" ? (
          // A1 — real summative quiz: the same item runner as /pe/quiz, embedded
          // (no page chrome). Per-item /pe/submit scores via studentAssignmentId;
          // the host's "Mark complete & continue" below owns step completion
          // (one progress row — FormativeAssessment never writes step progress).
          <FormativeAssessment
            source="quiz"
            role="student"
            embedded
            chapterId={chapterId}
            tier={tier}
            lang={lang}
            studentAssignmentId={studentAssignmentId ?? null}
          />
        ) : activeStep ? (
          // Defensive fallback — every current StepId is handled above, so this
          // is unreachable; kept for any future step added without a surface.
          <StepPanel label={t.continueLabel} blurb={t.continueBlurb} hint={t.stepPanelHint(t.markComplete)} />
        ) : null}

        {/* Advance control — only on the CURRENT step (engine enforces order).
            D4: the formative step gates this on the embedded component signaling
            all items answered (formativeDone), so the step can't complete early. */}
        {isViewingCurrent && activeStep && !view.isComplete && (
          activeStep === "formative" && !formativeDone ? (
            <p className="mt-6 text-right text-sm" style={{ color: "#6b7280" }}>
              {t.answerEveryItem}
            </p>
          ) : (
            <div className="mt-6 flex justify-end">
              <button
                type="button"
                disabled={saving}
                onClick={completeCurrentStep}
                className="rounded-lg bg-indigo-600 px-6 py-3 text-white font-semibold hover:bg-indigo-700 disabled:opacity-60"
              >
                {saving ? t.saving : t.markComplete}
              </button>
            </div>
          )
        )}

        {!isViewingCurrent && activeStep && (
          <div className="mt-6 flex justify-end">
            <button
              type="button"
              onClick={() => setViewing(view.currentStep)}
              className="rounded-lg bg-indigo-50 px-6 py-3 text-indigo-700 font-semibold hover:bg-indigo-100"
            >
              {t.backToCurrent}
            </button>
          </div>
        )}
      </main>
    </div>
  );
};

/** Placeholder surface for steps whose dedicated content player isn't built yet. */
const StepPanel: React.FC<{ label: string; blurb: string; hint: string }> = ({ label, blurb, hint }) => (
  <div className="rounded-xl bg-white border border-gray-200 p-8 text-center">
    <h2 className="text-2xl font-bold text-gray-900">{label}</h2>
    <p className="mt-2 text-gray-600 max-w-prose mx-auto">{blurb}</p>
    <p className="mt-6 text-sm text-gray-400">{hint}</p>
  </div>
);

export default LessonSequence;
